import type { ReactNode } from 'react'
import { motion, useAnimate, useReducedMotion } from 'motion/react'

/**
 * 生成鈕的果凍回彈（USER 2026-10-13・與 Mugen_H3 批 jelly 同一組手感、各自維護不共用程式碼）：
 * 按下去先微縮（`PRESS_SCALE`・`PRESS_IN_TRANSITION`）、放開那一刻硬彈簧回 1（`PRESS_OUT_TRANSITION`）
 * 並播一段果凍抖動——橫向拉寬時縱向壓扁、反向彈回、三下收斂（`JELLY_KEYFRAMES`・`JELLY_TRANSITION`）。
 * 手感旋鈕全在下面這幾個常數。
 *
 * - 首尾皆 1＝播完回到原尺寸（停在 1 以外的值會讓 `.generate-row` 永久歪）。
 * - 抖動走 `useAnimate` 指令式：鈕點下去的下一瞬間就會變 disabled（submitting），
 *   綁在 `animate` prop 上會跟著 re-render 被截斷；指令式一旦起跑就播完。
 * - 系統「減少動態效果」＝只保留微縮、不播抖動。
 * - 標籤／class／disabled 語意與原生 `<button type="button" className="generate-button">` 相同；
 *   Motion 的按壓手勢會補 `tabindex="0"`（按鈕本就可聚焦、disabled 時仍不可聚焦＝無副作用）。
 */
export const PRESS_SCALE = 0.94
/** 按下去縮到 PRESS_SCALE：約 60ms 到位。 */
export const PRESS_IN_TRANSITION = { type: 'tween', duration: 0.06, ease: 'easeOut' } as const
/** 放開回到 1：硬彈簧、約 76ms 回到 1、過衝 0.4%。 */
export const PRESS_OUT_TRANSITION = { type: 'spring', stiffness: 1500, damping: 50 } as const
export const JELLY_KEYFRAMES = {
  scaleX: [1, 1.12, 0.92, 1.05, 0.98, 1],
  scaleY: [1, 0.88, 1.08, 0.96, 1.02, 1],
} as const
/** `times`＝每格關鍵影格落在整段的比例：第一段拉寬 60ms、後四段各 110ms。 */
export const JELLY_TRANSITION = {
  duration: 0.5,
  ease: 'easeOut' as const,
  times: [0, 0.12, 0.34, 0.56, 0.78, 1] as number[],
}

type JellyGenerateButtonProps = {
  disabled?: boolean
  onClick?: () => void
  className?: string
  children: ReactNode
}

export function JellyGenerateButton({ disabled = false, onClick, className = 'generate-button', children }: JellyGenerateButtonProps) {
  const [jellyScope, animateJelly] = useAnimate<HTMLButtonElement>()
  const reducedMotion = useReducedMotion()
  const handleClick = (): void => {
    const el = jellyScope.current
    if (el !== null) {
      // 鍵盤 Enter 的順序是 keydown（按壓開始・縮）→ click → 鈕 disabled → keyup：Motion 的按壓收尾遇到 disabled 鈕直接返回
      // （framer-motion gestures/press handlePressEvent），whileTap 狀態留在「按著」、鈕卡在 PRESS_SCALE、下一次按壓也不再縮。
      // 解法＝趁鈕還沒被停用（click 先於 disabled）先替 Motion 補發一次 pointerup 讓它正常收尾：鍵盤路徑＝結束 keydown 那次按壓；
      // 滑鼠路徑＝真的 pointerup 早已收尾、window 監聽已拆＝這一發無人接、無副作用。
      if (typeof PointerEvent !== 'undefined') {
        el.dispatchEvent(new PointerEvent('pointerup', { isPrimary: true, bubbles: true }))
      }
      // 再把 scale 帶回 1（同一次 animate・同一組硬彈簧）當保險；減少動態效果＝只帶回 1、不播果凍。
      const target = reducedMotion === true ? { scale: 1 } : { ...JELLY_KEYFRAMES, scale: 1 }
      animateJelly(el, target, { ...JELLY_TRANSITION, scale: PRESS_OUT_TRANSITION })
    }
    onClick?.()
  }
  return (
    <motion.button
      ref={jellyScope}
      type="button"
      className={className}
      disabled={disabled}
      onClick={handleClick}
      whileTap={{ scale: PRESS_SCALE, transition: PRESS_IN_TRANSITION }}
      transition={{ scale: PRESS_OUT_TRANSITION }}
    >
      {children}
    </motion.button>
  )
}
